import { computed, inject, Injectable, PLATFORM_ID, REQUEST } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { environment } from '@/environments/environment';
import { isAdminPath, isMetrikaHostAllowed } from '@/services/metrika/metrika.utils';
import type { YmHitOptions, YmInitOptions } from '@/services/metrika/metrika.types';

const METRIKA_SCRIPT_BASE = 'https://mc.yandex.ru/metrika/tag.js';

const YM_INIT_OPTIONS: YmInitOptions = {
  defer: true,
  webvisor: true,
  clickmap: true,
  trackLinks: true,
  accurateTrackBounce: true,
};

interface PendingHit {
  url: string;
  options?: YmHitOptions;
}

@Injectable({ providedIn: 'root' })
export class MetrikaService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly request = inject(REQUEST, { optional: true }) as {
    hostname?: string;
    headers?: Headers | Record<string, string | string[] | undefined>;
  } | null;

  private readonly isBrowser = isPlatformBrowser(this.platformId);

  /** Same gates as canRun(), but SSR-safe (no browser-only check). */
  readonly noscriptVisible = computed(() => this.shouldShowNoscript());

  private initialized = false;
  private ready = false;
  private initPromise: Promise<void> | null = null;
  private pendingHits: PendingHit[] = [];

  canRun(): boolean {
    if (!this.isBrowser) {
      return false;
    }
    return this.shouldShowNoscript();
  }

  shouldShowNoscript(): boolean {
    if (!environment.metrika.enabled) {
      return false;
    }
    const hostname = this.resolveHostname();
    if (!hostname || !isMetrikaHostAllowed(hostname, environment.metrika.allowedHosts)) {
      return false;
    }
    if (isAdminPath(this.router.url)) {
      return false;
    }
    return true;
  }

  init(): Promise<void> {
    if (!this.canRun()) {
      return Promise.resolve();
    }
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.loadScriptAndInit();
    return this.initPromise;
  }

  hit(url: string, options?: YmHitOptions): void {
    if (!this.canRun() || isAdminPath(url)) {
      return;
    }
    if (!this.initialized && !this.initPromise) {
      return;
    }
    if (!this.ready) {
      this.pendingHits.push({ url, options });
      return;
    }

    this.sendHit(url, options);
  }

  destruct(): void {
    if (!this.isBrowser || !this.initialized) {
      return;
    }

    const ym = window.ym;
    if (typeof ym === 'function') {
      ym(environment.metrika.counterId, 'destruct');
    }

    this.initialized = false;
    this.ready = false;
    this.initPromise = null;
    this.pendingHits = [];
  }

  private async loadScriptAndInit(): Promise<void> {
    // Standard Metrika preamble must run BEFORE tag.js loads.
    // tag.js checks window.ym.l (timestamp) to detect proper initialization;
    // without it the script loads but window.ym remains undefined.
    this.setupYmPreamble();
    this.callYmInit(); // queue the init call — tag.js processes it on load
    await this.loadScript();
    this.initialized = true;
    this.ready = true;
    this.flushPendingHits();
  }

  private setupYmPreamble(): void {
    if (window.ym) {
      return;
    }

    const queue: IArguments[] = [];
    const fn = Object.assign(
      function (this: unknown) {
        queue.push(arguments);
      },
      { a: queue, l: Date.now() },
    );
    window.ym = fn as unknown as NonNullable<Window['ym']>;
  }

  private loadScript(): Promise<void> {
    const scriptUrl = METRIKA_SCRIPT_BASE;

    const existingScript = Array.from(document.scripts).find((script) => script.src === scriptUrl);
    if (existingScript) {
      if (existingScript.dataset['metrikaLoaded'] === 'true') {
        return Promise.resolve();
      }
      return new Promise<void>((resolve, reject) => {
        existingScript.addEventListener('load', () => resolve(), { once: true });
        existingScript.addEventListener(
          'error',
          () => reject(new Error('Metrika script failed to load')),
          {
            once: true,
          },
        );
      });
    }

    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.addEventListener(
        'load',
        () => {
          script.dataset['metrikaLoaded'] = 'true';
          resolve();
        },
        { once: true },
      );
      script.addEventListener('error', () => reject(new Error('Metrika script failed to load')), {
        once: true,
      });
      document.head.appendChild(script);
    });
  }

  private callYmInit(): void {
    const ym = window.ym;
    if (typeof ym !== 'function') {
      return;
    }
    ym(environment.metrika.counterId, 'init', YM_INIT_OPTIONS);
  }

  private sendHit(url: string, options?: YmHitOptions): void {
    const ym = window.ym;
    if (typeof ym !== 'function') {
      return;
    }
    ym(environment.metrika.counterId, 'hit', url, options);
  }

  private flushPendingHits(): void {
    const hits = this.pendingHits;
    this.pendingHits = [];
    for (const { url, options } of hits) {
      if (!isAdminPath(url)) {
        this.sendHit(url, options);
      }
    }
  }

  private resolveHostname(): string | null {
    if (this.isBrowser && typeof window !== 'undefined') {
      return window.location.hostname;
    }

    if (typeof this.request?.hostname === 'string') {
      return this.request.hostname;
    }

    const headers = this.request?.headers;
    if (!headers) {
      return null;
    }

    let host: string | null;
    if (typeof (headers as Headers).get === 'function') {
      host = (headers as Headers).get('host');
    } else {
      const record = headers as Record<string, string | string[] | undefined>;
      const value = record['host'] ?? record['Host'];
      host = Array.isArray(value) ? (value[0] ?? null) : (value ?? null);
    }

    if (!host) {
      return null;
    }

    return host.split(':')[0];
  }
}
